import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import api from "../lib/api";
import { useAuth } from "./AuthContext";

const CartContext = createContext(null);
const STORAGE_KEY = "cc_cart";

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || []; } catch { return []; }
  });
  const [open, setOpen] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  useEffect(() => {
    if (!user) return;
    const t = setTimeout(() => {
      api.post("/cart/snapshot", {
        items: items.map((i) => ({ name: i.name, qty: i.qty, unit_price: i.unit_price, image: i.image || "" })),
      }).catch(() => {});
    }, 2000);
    return () => clearTimeout(t);
  }, [items, user]);

  const addItem = useCallback((item) => {
    setItems((prev) => {
      const key = `${item.product_id}|${item.size}|${item.color}|${item.customization?.method || ""}|${item.customization?.placement || ""}|${item.customization?.text || ""}`;
      const idx = prev.findIndex((p) => p.key === key);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], qty: next[idx].qty + item.qty };
        return next;
      }
      return [...prev, { ...item, key }];
    });
    setOpen(true);
  }, []);

  const removeItem = useCallback((key) => setItems((prev) => prev.filter((p) => p.key !== key)), []);

  const updateQty = useCallback((key, qty) => {
    setItems((prev) => qty <= 0 ? prev.filter((p) => p.key !== key) : prev.map((p) => (p.key === key ? { ...p, qty } : p)));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const total = useMemo(() => items.reduce((s, i) => s + i.unit_price * i.qty, 0), [items]);
  const count = useMemo(() => items.reduce((s, i) => s + i.qty, 0), [items]);

  return (
    <CartContext.Provider value={{ items, addItem, removeItem, updateQty, clear, total, count, open, setOpen }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
